import React, { useState, useEffect, useId } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Segmented } from "@/components/ui/segmented";
import { addPatient, searchPatients, updatePatient } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  User, Phone, Mail, Calendar, AlertTriangle, UserPlus,
  Loader2, Users, CheckCircle, Stethoscope, X, Save,
  Heart, MapPin, Edit, UserCheck, ShieldAlert, CreditCard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

interface FormData {
  name: string;
  age: string;
  age_unit: 'years' | 'months' | 'days';
  date_of_birth: string;
  gender: string;
  phone: string;
  phone_2: string;
  email: string;
  address: string;
  city: string;
  pin_code: string;
  preferred_language: string;
  aadhar_number: string;
  blood_group: string;
  allergies: string;
  chronic_conditions: string;
  current_medications: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  payment_preference: string;
}


interface PotentialDuplicate {
  id: string; name: string; age?: number; gender?: string; phone?: string; email?: string;
  match_type: 'exact_name' | 'exact_phone';
}

interface SmartDuplicateCheck {
  has_duplicates: boolean;
  duplicates: PotentialDuplicate[];
  can_proceed: boolean;
  message?: string;
}

type SubmitIntent = 'patient-only' | 'patient-and-rx' | 'patient-and-bill' | 'patient-and-queue';

interface PatientFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newPatient: any, intent: SubmitIntent) => void;
  title?: string;
  showPrescriptionOption?: boolean;
  showQueueOption?: boolean;
  mode?: 'create' | 'edit';
  initialData?: Partial<FormData>;
  patientId?: string;
  compact?: boolean;
  /** Create: what was typed in a patient search — fills Phone when it's a 10-digit number, otherwise Name */
  initialSearch?: string;
}

const EMPTY_FORM: FormData = {
  name: "", age: "", age_unit: "years", date_of_birth: "", gender: "",
  phone: "", phone_2: "", email: "",
  address: "", city: "", pin_code: "",
  preferred_language: "English", aadhar_number: "",
  blood_group: "", allergies: "", chronic_conditions: "", current_medications: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  payment_preference: "Cash",
};

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 pt-1">
      <span className="text-2xs font-semibold text-muted-foreground uppercase tracking-widest whitespace-nowrap">{label}</span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

function FieldRow({ label, icon: Icon, required, children, error, htmlFor }: {
  label: string; icon?: React.ElementType; required?: boolean; children: React.ReactNode; error?: string;
  /** id of the field the label names — clicking the label focuses it, screen readers announce it */
  htmlFor?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3 text-muted-foreground" />}
        <Label htmlFor={htmlFor} className="text-xs font-medium text-foreground">
          {label}{required && <span className="text-destructive ml-0.5">*</span>}
        </Label>
      </div>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default function PatientFormModal({
  isOpen, onClose, onSuccess,
  title = "New patient",
  showPrescriptionOption = true,
  showQueueOption = false,
  mode = 'create',
  initialData, patientId,
  compact = true,
  initialSearch,
}: PatientFormModalProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isDoctor = !!currentUser?.doctor_id;

  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [duplicateCheck, setDuplicateCheck] = useState<SmartDuplicateCheck | null>(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [userConfirmedDuplicate, setUserConfirmedDuplicate] = useState(false);
  // Edit: the values as loaded, so "Save changes" stays off until something changes
  const [loadedSnapshot, setLoadedSnapshot] = useState<string | null>(null);
  const uid = useId();
  const fid = (key: string) => `${uid}-${key}`;

  // Create from a search: start with what was typed, so it isn't typed twice
  useEffect(() => {
    if (!isOpen || mode !== 'create') return;
    const term = (initialSearch ?? "").trim();
    if (!term) return;
    const digits = term.replace(/\D/g, "");
    setFormData(prev => /^\+?[\d\s-]+$/.test(term) && digits.length >= 10
      ? { ...prev, phone: digits.slice(-10) }
      : { ...prev, name: term });
  }, [isOpen, mode, initialSearch]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setValidationErrors({});
      setDuplicateCheck(null);
      setCheckingDuplicates(false);
      setUserConfirmedDuplicate(false);
      setLoadedSnapshot(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && mode === 'edit' && initialData) {
      // If DOB is present, compute age from it (same peds-aware logic as handleDobChange)
      // so we don't show stale stored age (e.g. "0 years" for an 8-month-old)
      let editAge = initialData.age?.toString() || "";
      let editUnit: 'years' | 'months' | 'days' = "years";
      const dob = initialData.date_of_birth || "";
      if (dob) {
        const birth = new Date(dob);
        if (!isNaN(birth.getTime())) {
          const today = new Date();
          const msPerDay = 1000 * 60 * 60 * 24;
          const totalDays = Math.floor((today.getTime() - birth.getTime()) / msPerDay);
          if (totalDays >= 0) {
            if (totalDays < 30) {
              editAge = totalDays.toString(); editUnit = "days";
            } else {
              const totalMonths =
                (today.getFullYear() - birth.getFullYear()) * 12 +
                (today.getMonth() - birth.getMonth()) -
                (today.getDate() < birth.getDate() ? 1 : 0);
              if (totalMonths < 24) {
                editAge = totalMonths.toString(); editUnit = "months";
              } else {
                editAge = Math.floor(totalMonths / 12).toString(); editUnit = "years";
              }
            }
          }
        }
      }

      const loaded: FormData = {
        name: initialData.name || "",
        age: editAge,
        age_unit: editUnit,
        date_of_birth: dob,
        gender: initialData.gender || "Male",
        phone: initialData.phone || "",
        phone_2: initialData.phone_2 || "",
        email: initialData.email || "",
        address: initialData.address || "",
        city: initialData.city || "",
        pin_code: initialData.pin_code || "",
        preferred_language: initialData.preferred_language || "English",
        aadhar_number: initialData.aadhar_number || "",
        blood_group: initialData.blood_group || "",
        allergies: initialData.allergies || "",
        chronic_conditions: initialData.chronic_conditions || "",
        current_medications: initialData.current_medications || "",
        emergency_contact_name: initialData.emergency_contact_name || "",
        emergency_contact_phone: initialData.emergency_contact_phone || "",
        payment_preference: initialData.payment_preference || "Cash",
      };
      setFormData(loaded);
      setLoadedSnapshot(JSON.stringify(loaded));
    }
  }, [isOpen, mode, initialData]);

  useEffect(() => {
    const check = async () => {
      if (mode === 'edit') return;

      const name  = formData.name.trim();
      const phone = formData.phone.trim();
      const hasName  = name.length >= 3;
      const hasPhone = phone.length === 10;

      if (!hasName && !hasPhone) { setDuplicateCheck(null); return; }

      setCheckingDuplicates(true);
      setDuplicateCheck(null);

      try {
        const duplicates: PotentialDuplicate[] = [];

        // Phone check — independent search by phone number (exact match only)
        if (hasPhone) {
          const phoneResults = await searchPatients(phone, currentUser?.clinic_id);
          (Array.isArray(phoneResults) ? phoneResults : []).forEach((p: any) => {
            if ((p.phone || '').replace(/\D/g, '') === phone) {
              duplicates.push({ id: p.id, name: p.name, age: p.age, gender: p.gender, phone: p.phone, match_type: 'exact_phone' });
            }
          });
        }

        // Name check — exact case-insensitive match only, no fuzzy
        if (hasName) {
          const nameResults = await searchPatients(name, currentUser?.clinic_id);
          const nameLower = name.toLowerCase();
          (Array.isArray(nameResults) ? nameResults : []).forEach((p: any) => {
            if (duplicates.some(d => d.id === p.id)) return; // already caught by phone
            if ((p.name || '').toLowerCase() === nameLower) {
              duplicates.push({ id: p.id, name: p.name, age: p.age, gender: p.gender, phone: p.phone, match_type: 'exact_name' });
            }
          });
        }

        if (duplicates.length > 0) {
          // Phone duplicates first
          duplicates.sort((a, b) => (a.match_type === 'exact_phone' ? -1 : 1));
          const message = duplicates[0].match_type === 'exact_phone'
            ? "This phone number is already registered"
            : "A patient with this name already exists";
          setDuplicateCheck({ has_duplicates: true, duplicates, can_proceed: true, message });
        } else {
          setDuplicateCheck(null);
        }
      } catch {
        setDuplicateCheck(null);
      } finally {
        setCheckingDuplicates(false);
      }
    };

    const t = setTimeout(check, 600);
    return () => clearTimeout(t);
  }, [formData.name, formData.phone, mode]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Name
    if (!formData.name.trim()) errors.name = "Patient name is required";
    else if (formData.name.trim().length < 2) errors.name = "Name must be at least 2 characters";

    // Primary phone
    if (!formData.phone.trim()) errors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(formData.phone)) errors.phone = "Must be exactly 10 digits";

    // Gender
    if (!formData.gender) errors.gender = "Gender is required";

    // Age / DOB
    if (!formData.age.trim() && !formData.date_of_birth.trim()) {
      errors.age = "Age or Date of Birth is required";
    } else if (formData.date_of_birth) {
      const dob = new Date(formData.date_of_birth);
      if (!isNaN(dob.getTime()) && dob > new Date()) {
        errors.date_of_birth = "Date of birth cannot be in the future";
      }
    } else if (formData.age) {
      const n = parseInt(formData.age);
      if (isNaN(n) || n < 0)                              errors.age = "Enter a valid age";
      else if (formData.age_unit === 'days'   && n > 29)  errors.age = "Use Months for infants 30 days and older";
      else if (formData.age_unit === 'months' && n > 23)  errors.age = "Use Years for children 2 years and older";
      else if (formData.age_unit === 'years'  && n > 120) errors.age = "Enter a valid age (0–120)";
    }

    // Alt phone
    if (formData.phone_2.trim()) {
      if (!/^\d{10}$/.test(formData.phone_2)) errors.phone_2 = "Must be exactly 10 digits";
      else if (formData.phone_2 === formData.phone) errors.phone_2 = "Must differ from primary phone";
    }

    // Email
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Enter a valid email address";
    }

    // Pin code
    if (formData.pin_code && !/^\d{6}$/.test(formData.pin_code)) {
      errors.pin_code = "Must be exactly 6 digits";
    }

    // Emergency contact — name and phone should be paired
    if (formData.emergency_contact_name.trim() && !formData.emergency_contact_phone.trim()) {
      errors.emergency_contact_phone = "Phone required when contact name is provided";
    }
    if (formData.emergency_contact_phone.trim()) {
      if (!/^\d{10}$/.test(formData.emergency_contact_phone)) {
        errors.emergency_contact_phone = "Must be exactly 10 digits";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (intent: SubmitIntent) => {
    if (!validateForm()) { toast({ title: "Please fix the validation errors", variant: "destructive" }); return; }
    if (mode === 'create' && duplicateCheck?.has_duplicates && !userConfirmedDuplicate) {
      toast({ title: "Please Confirm", description: "Similar patients found. Review and confirm this is a different person.", variant: "default" });
      return;
    }
    setLoading(true);
    try {
      const patientData = {
        name: formData.name.trim(),
        age: formData.age
          ? formData.age_unit === 'days'   ? 0                                       // neonate < 1y
          : formData.age_unit === 'months' ? Math.floor(parseInt(formData.age) / 12) // store as years
          : parseInt(formData.age)
          : null,
        date_of_birth: formData.date_of_birth || null, gender: formData.gender,
        phone: formData.phone.trim(), phone_2: formData.phone_2.trim() || null,
        email: formData.email.trim() || null, address: formData.address.trim() || null,
        city: formData.city.trim() || null, pin_code: formData.pin_code.trim() || null,
        preferred_language: formData.preferred_language,
        aadhar_number: formData.aadhar_number.trim() || null,
        blood_group: formData.blood_group || null,
        allergies: formData.allergies.trim() || null,
        chronic_conditions: formData.chronic_conditions.trim() || null,
        current_medications: formData.current_medications.trim() || null,
        emergency_contact_name: formData.emergency_contact_name.trim() || null,
        emergency_contact_phone: formData.emergency_contact_phone.trim() || null,
        payment_preference: formData.payment_preference,
        doctor_id: currentUser?.doctor_id, clinic_id: currentUser?.clinic_id,
      };
      let result;
      if (mode === 'edit' && patientId) {
        const response = await updatePatient(patientId, patientData);
        if (!response?.id) throw new Error("Failed to update patient");
        result = { ...response, ...patientData };
        toast({ title: "Patient Updated", description: `${formData.name}'s details have been updated.` });
      } else {
        const apiResponse = await addPatient(patientData);
        if (!apiResponse?.id) throw new Error("Failed to create patient");
        result = { id: apiResponse.id, ...patientData, created_at: new Date().toISOString(), updated_at: new Date().toISOString() };
        const intentText = intent === 'patient-and-rx' ? 'and prescription ready' : intent === 'patient-and-bill' ? 'and bill ready' : intent === 'patient-and-queue' ? 'and added to queue' : 'successfully';
        toast({ title: "Patient Added", description: `${formData.name} registered ${intentText}.` });
      }
      if (intent === 'patient-and-rx') { onClose(); navigate(`/patients/${result.id}?openPrescription=true`); return; }
      if (intent === 'patient-and-queue') { onSuccess?.(result, intent); onClose(); return; }
      onSuccess?.(result, intent); onClose();
    } catch (error) {
      console.error(`Failed to ${mode} patient:`, error);
      toast({ title: `Failed to ${mode} patient`, description: "Please try again.", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const handleConfirmDuplicate = () => {
    setUserConfirmedDuplicate(true);
    toast({ title: "Confirmed", description: "You can now proceed to add this as a new patient." });
  };

  const updateFormData = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) setValidationErrors(prev => ({ ...prev, [field]: "" }));
    if ((field === 'name' || field === 'phone') && userConfirmedDuplicate) setUserConfirmedDuplicate(false);
  };

  // DOB → auto-compute age with full peds awareness:
  //   < 30 days   → show in days   (neonates)
  //   30–729 days → show in months (infants/toddlers)
  //   ≥ 730 days  → show in years
  const handleDobChange = (value: string) => {
    let ageValue = "";
    let ageUnit: 'years' | 'months' | 'days' = "years";
    if (value) {
      const birth = new Date(value);
      if (!isNaN(birth.getTime())) {
        const today = new Date();
        const msPerDay = 1000 * 60 * 60 * 24;
        const totalDays = Math.floor((today.getTime() - birth.getTime()) / msPerDay);
        if (totalDays < 0) {
          ageValue = ""; // future DOB — invalid
        } else if (totalDays < 30) {
          ageValue = totalDays.toString();
          ageUnit = "days";
        } else {
          const totalMonths =
            (today.getFullYear() - birth.getFullYear()) * 12 +
            (today.getMonth() - birth.getMonth()) -
            (today.getDate() < birth.getDate() ? 1 : 0);
          if (totalMonths < 24) {
            ageValue = totalMonths.toString();
            ageUnit = "months";
          } else {
            ageValue = Math.floor(totalMonths / 12).toString();
            ageUnit = "years";
          }
        }
      }
    }
    setFormData(prev => ({ ...prev, date_of_birth: value, age: ageValue || prev.age, age_unit: ageUnit }));
    if (validationErrors.age) setValidationErrors(prev => ({ ...prev, age: "" }));
  };

  // Manual age entry → clear DOB; user controls unit via selector
  const handleAgeChange = (value: string) => {
    setFormData(prev => ({ ...prev, age: value, date_of_birth: "" }));
    if (validationErrors.age) setValidationErrors(prev => ({ ...prev, age: "" }));
  };

  const unchanged = mode === 'edit' && loadedSnapshot !== null && JSON.stringify(formData) === loadedSnapshot;
  const canSubmit = !loading && !checkingDuplicates && formData.name.trim() && formData.phone.trim() && formData.gender && (formData.age.trim() || formData.date_of_birth.trim());
  // The footer names what's still missing rather than pointing at the asterisks
  const missing = [
    !formData.name.trim() && "name", !formData.phone.trim() && "phone", !formData.gender && "gender",
    !(formData.age.trim() || formData.date_of_birth.trim()) && "age",
  ].filter(Boolean);
  const stillNeeded = missing.length ? `Still needed: ${missing.join(", ")}` : "Fill in the required fields (*)";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] p-0 flex flex-col overflow-hidden gap-0">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <DialogHeader className="flex-shrink-0 px-5 pt-4 pb-3 border-b border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              {mode === 'edit' ? <Edit className="w-4 h-4 text-primary" /> : <UserPlus className="w-4 h-4 text-primary" />}
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-sm font-semibold text-foreground">
                {mode === 'edit' ? 'Edit patient' : title}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                {mode === 'edit' ? 'Modify patient details' : 'Register a new patient'}
              </DialogDescription>
            </div>
            {checkingDuplicates && mode === 'create' && (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground ml-auto" />
            )}
          </div>
        </DialogHeader>

        {/* ── Scrollable body ─────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">

          {/* ── Form fields ─────────────────────────────────────────── */}
          <div className="px-5 py-4 space-y-5">

            {/* ── Identity ─────────────────────────────────────────── */}
            <SectionDivider label="Identity" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Name field with inline duplicate hint */}
              <div className="space-y-1">
                <FieldRow label="Full name" icon={User} required error={validationErrors.name} htmlFor={fid("name")}>
                  <Input id={fid("name")} value={formData.name} onChange={e => updateFormData('name', e.target.value)}
                    placeholder="Patient's full name"
                    className={cn("h-9 text-sm", validationErrors.name && "border-destructive",
                      mode === 'create' && !userConfirmedDuplicate && duplicateCheck?.duplicates.some(d => d.match_type === 'exact_name') && "border-warning")} />
                </FieldRow>
                {mode === 'create' && !userConfirmedDuplicate && (() => {
                  const match = duplicateCheck?.duplicates.find(d => d.match_type === 'exact_name');
                  if (!match) return null;
                  return (
                    <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-warning/5 border border-warning/20 rounded-md">
                      <div className="flex items-center gap-1.5 text-xs min-w-0">
                        <AlertTriangle className="w-3 h-3 text-warning flex-shrink-0" />
                        <span className="text-warning font-medium truncate">{match.name}</span>
                        {match.age && <span className="text-muted-foreground flex-shrink-0">· {match.age}y {match.gender}</span>}
                      </div>
                      <button onClick={handleConfirmDuplicate} className="text-xs text-primary hover:underline flex-shrink-0 font-medium">
                        Different person
                      </button>
                    </div>
                  );
                })()}
              </div>

              {/* Phone field with inline duplicate hint */}
              <div className="space-y-1">
                <FieldRow label="Phone" icon={Phone} required error={validationErrors.phone} htmlFor={fid("phone")}>
                  <Input id={fid("phone")} value={formData.phone} onChange={e => updateFormData('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit mobile" maxLength={10}
                    className={cn("h-9 text-sm", validationErrors.phone && "border-destructive",
                      mode === 'create' && !userConfirmedDuplicate && duplicateCheck?.duplicates.some(d => d.match_type === 'exact_phone') && "border-warning")} />
                </FieldRow>
                {mode === 'create' && !userConfirmedDuplicate && (() => {
                  const match = duplicateCheck?.duplicates.find(d => d.match_type === 'exact_phone');
                  if (!match) return null;
                  return (
                    <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-destructive/5 border border-destructive/20 rounded-md">
                      <div className="flex items-center gap-1.5 text-xs min-w-0">
                        <AlertTriangle className="w-3 h-3 text-destructive flex-shrink-0" />
                        <span className="text-destructive font-medium truncate">{match.name}</span>
                        {match.age && <span className="text-muted-foreground flex-shrink-0">· {match.age}y {match.gender}</span>}
                      </div>
                      <button onClick={handleConfirmDuplicate} className="text-xs text-primary hover:underline flex-shrink-0 font-medium">
                        Different person
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldRow label="Gender" required error={validationErrors.gender}>
                <Segmented
                  aria-label="Gender"
                  className="flex w-full h-9 touch:h-10"
                  options={["Male", "Female", "Other"].map(g => ({ value: g, label: g }))}
                  value={formData.gender}
                  onChange={v => updateFormData('gender', v)}
                />
              </FieldRow>

              {/* Age / DOB — peds-aware */}
              <FieldRow label="Age or date of birth" icon={Calendar} required error={validationErrors.age || validationErrors.date_of_birth} htmlFor={fid("age")}>
                <div className="flex flex-wrap gap-2 items-start">
                  <div className="flex flex-col">
                    <Input
                      id={fid("age")}
                      type="number"
                      value={formData.age}
                      onChange={e => handleAgeChange(e.target.value)}
                      disabled={!!formData.date_of_birth}
                      placeholder="Age"
                      min="0"
                      max={formData.age_unit === 'days' ? "29" : formData.age_unit === 'months' ? "23" : "120"}
                      className={cn("w-16 h-9 text-sm",
                        validationErrors.age && "border-destructive",
                        formData.date_of_birth && "bg-muted text-muted-foreground"
                      )}
                    />
                  </div>

                  {/* Unit: manual = Years/Months only; DOB-computed shows label */}
                  {!formData.date_of_birth ? (
                    <Select
                      value={formData.age_unit === 'days' ? 'years' : formData.age_unit}
                      onValueChange={v => setFormData(prev => ({ ...prev, age_unit: v as 'years' | 'months' }))}
                    >
                      <SelectTrigger aria-label="Age in" className="w-24 h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="years">Years</SelectItem>
                        <SelectItem value="months">Months</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="h-9 flex items-center text-xs text-muted-foreground capitalize px-1">
                      {formData.age_unit}
                    </span>
                  )}

                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={e => handleDobChange(e.target.value)}
                    aria-label="Date of birth"
                    max={(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })()}
                    className="flex-1 min-w-[9rem] h-9 text-sm"
                  />
                </div>
              </FieldRow>
            </div>

            {/* ── Medical ──────────────────────────────────────────── */}
            <SectionDivider label="Medical" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldRow label="Blood group" icon={Heart} htmlFor={fid("blood_group")}>
                <Select value={formData.blood_group} onValueChange={v => updateFormData('blood_group', v)}>
                  <SelectTrigger id={fid("blood_group")} className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label="Allergies" icon={ShieldAlert} htmlFor={fid("allergies")}>
                <Input id={fid("allergies")} value={formData.allergies} onChange={e => updateFormData('allergies', e.target.value)}
                  placeholder="e.g. Penicillin, Sulfa" className="h-9 text-sm" />
              </FieldRow>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldRow label="Long-term conditions" htmlFor={fid("chronic_conditions")}>
                <Input id={fid("chronic_conditions")} value={formData.chronic_conditions} onChange={e => updateFormData('chronic_conditions', e.target.value)}
                  placeholder="e.g. Type 2 diabetes, Hypertension" maxLength={500} className="h-9 text-sm" />
              </FieldRow>
              <FieldRow label="Current medicines" htmlFor={fid("current_medications")}>
                <Input id={fid("current_medications")} value={formData.current_medications} onChange={e => updateFormData('current_medications', e.target.value)}
                  placeholder="e.g. Metformin 500 mg 1-0-1" maxLength={500} className="h-9 text-sm" />
              </FieldRow>
            </div>

            {/* ── Contact ──────────────────────────────────────────── */}
            <SectionDivider label="Contact" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldRow label="Alternate phone" icon={Phone} error={validationErrors.phone_2} htmlFor={fid("phone_2")}>
                <Input id={fid("phone_2")} value={formData.phone_2} onChange={e => updateFormData('phone_2', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Secondary number" maxLength={10}
                  className={cn("h-9 text-sm", validationErrors.phone_2 && "border-destructive")} />
              </FieldRow>
              <FieldRow label="Email" icon={Mail} error={validationErrors.email} htmlFor={fid("email")}>
                <Input id={fid("email")} type="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)}
                  placeholder="Email address"
                  className={cn("h-9 text-sm", validationErrors.email && "border-destructive")} />
              </FieldRow>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldRow label="City" icon={MapPin} htmlFor={fid("city")}>
                <Input id={fid("city")} value={formData.city} onChange={e => updateFormData('city', e.target.value)}
                  placeholder="City / District" className="h-9 text-sm" />
              </FieldRow>
              <FieldRow label="PIN code" error={validationErrors.pin_code} htmlFor={fid("pin_code")}>
                <Input id={fid("pin_code")} value={formData.pin_code} onChange={e => updateFormData('pin_code', e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit PIN" maxLength={6}
                  className={cn("h-9 text-sm", validationErrors.pin_code && "border-destructive")} />
              </FieldRow>
            </div>
            <FieldRow label="Address" icon={MapPin} htmlFor={fid("address")}>
              <Textarea id={fid("address")} value={formData.address} onChange={e => updateFormData('address', e.target.value)}
                placeholder="Street address" rows={2} className="resize-none text-sm" />
            </FieldRow>

            {/* ── Emergency Contact ────────────────────────────────── */}
            <SectionDivider label="Emergency contact" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldRow label="Name" htmlFor={fid("ec_name")}>
                <Input id={fid("ec_name")} value={formData.emergency_contact_name} onChange={e => updateFormData('emergency_contact_name', e.target.value)}
                  placeholder="Contact name" className="h-9 text-sm" />
              </FieldRow>
              <FieldRow label="Phone" icon={Phone} error={validationErrors.emergency_contact_phone} htmlFor={fid("ec_phone")}>
                <Input id={fid("ec_phone")} value={formData.emergency_contact_phone} onChange={e => updateFormData('emergency_contact_phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="10-digit number" maxLength={10}
                  className={cn("h-9 text-sm", validationErrors.emergency_contact_phone && "border-destructive")} />
              </FieldRow>
            </div>

            {/* ── Preferences ──────────────────────────────────────── */}
            <SectionDivider label="Preferences" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldRow label="Language" htmlFor={fid("language")}>
                <Select value={formData.preferred_language} onValueChange={v => updateFormData('preferred_language', v)}>
                  <SelectTrigger id={fid("language")} className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["English","Hindi","Marathi","Gujarati","Tamil","Telugu","Kannada","Malayalam","Bengali","Punjabi","Other"].map(l =>
                      <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label="Payment" icon={CreditCard} htmlFor={fid("payment")}>
                <Select value={formData.payment_preference} onValueChange={v => updateFormData('payment_preference', v)}>
                  <SelectTrigger id={fid("payment")} className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Cash","Insurance","Card","UPI","Other"].map(p =>
                      <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>
            </div>

          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-t border-border bg-card">
          <p className="text-xs text-muted-foreground">
            {mode === 'edit'
              ? (unchanged ? "No changes yet" : canSubmit ? "Ready to save" : "Fill in the required fields (*)")
              : checkingDuplicates ? "Checking for duplicates…"
              : duplicateCheck?.has_duplicates && !userConfirmedDuplicate ? "Confirm if different person"
              : canSubmit ? "Ready to save" : stillNeeded}
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={loading} className="h-8 px-3 text-xs">
              Cancel
            </Button>
            {mode === 'edit' ? (
              <Button size="sm" onClick={() => handleSubmit('patient-only')} disabled={!canSubmit || unchanged} className="h-8 px-4 text-xs gap-1.5">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save changes
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={() => handleSubmit('patient-only')}
                  disabled={!canSubmit || (duplicateCheck?.has_duplicates && !userConfirmedDuplicate)}
                  className="h-8 px-3 text-xs gap-1.5">
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                  Create
                </Button>
                {showPrescriptionOption && isDoctor && !showQueueOption && (
                  <Button size="sm" onClick={() => handleSubmit('patient-and-rx')}
                    disabled={!canSubmit || (duplicateCheck?.has_duplicates && !userConfirmedDuplicate)}
                    className="h-8 px-3 text-xs gap-1.5">
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Stethoscope className="w-3.5 h-3.5" />}
                    Create & Rx
                  </Button>
                )}
                {showQueueOption && mode === 'create' && (
                  <Button size="sm" onClick={() => handleSubmit('patient-and-queue')}
                    disabled={!canSubmit || (duplicateCheck?.has_duplicates && !userConfirmedDuplicate)}
                    className="h-8 px-3 text-xs gap-1.5">
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
                    Create & Queue
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}
